// src/pages/ArticlePage.js
import React, { useState, useEffect, useMemo, useCallback, Suspense } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import parse from "html-react-parser";
import { isValidElementType } from "react-is";

import SEO from "../components/SEO";
import { fetchContent } from "../content/content";
import { COMPONENT_REGISTRY, TAG_ALIASES } from "../render/registry";
import DOMPurifyLib from "dompurify";
import LoginModal from "../components/LoginModal";

import {
  addComment,
  addOptimistic,
  removeOptimistic,
  selectCommentsFor,
  setCommentsForContent,
} from '../content/commentsSlice';

import { selectAuthUser } from "../content/auth";

const BASE_URL = import.meta?.env?.VITE_SITE_URL || "https://werlocal.us";

// ---- sanitize links to open new tab
const DOMPurify =
  typeof window !== "undefined" && DOMPurifyLib ? DOMPurifyLib : { sanitize: (x) => x };
if (DOMPurify.addHook) {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

function stripText(html = "", maxLen = 220) {
  const el = document.createElement("div");
  el.innerHTML = html;
  const txt = (el.textContent || el.innerText || "").replace(/\s+/g, " ").trim();
  return txt.length > maxLen ? txt.slice(0, maxLen - 1) + "…" : txt;
}

function splitFirstParagraph(html) {
  const temp = document.createElement("div");
  temp.innerHTML = html || "";
  const firstP = temp.querySelector("p");
  let firstParagraph = "";
  if (firstP) {
    firstParagraph = firstP.outerHTML;
    firstP.remove();
  }
  return { firstParagraph, rest: temp.innerHTML };
}

function attribsToProps(attribs = {}) {
  const props = {};
  for (const [k, v] of Object.entries(attribs)) {
    if (k === "data-props") {
      try {
        Object.assign(props, JSON.parse(v));
      } catch {}
    } else if (k.startsWith("data-") && k !== "data-component") {
      const key = k.replace(/data-/, "").replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      props[key] = v;
    }
  }
  return props;
}

const parserOptions = {
  replace: (node) => {
    if (!node || node.type !== "tag") return;
    const compName = TAG_ALIASES[node.name] || node.attribs?.["data-component"];
    if (!compName) return;
    const Comp = COMPONENT_REGISTRY[compName];
    if (!isValidElementType(Comp)) {
      console.warn("Invalid element type for", compName, Comp);
      return null;
    }
    const props = attribsToProps(node.attribs || {});
    return <Comp {...props} />;
  },
};

function safeDate(v) {
  if (!v) return new Date();
  const s = String(v).trim().replace(" ", "T");
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function normalizeComments(arr = []) {
  const src = Array.isArray(arr) ? arr : [];
  return src
    .map((c) => ({
      id: String(c.id ?? crypto.randomUUID()),
      parent_id: c.comment_id ? String(c.comment_id) : null,
      user: c.user || c.author || "Guest",
      text: (c.text || c.body || "").trim(),
      dt: safeDate(c.dt),
    }))
    .filter((c) => c.text.length > 0);
}

function buildCommentTree(flat = []) {
  const byId = new Map();
  const roots = [];
  flat.forEach((c) => byId.set(c.id, { ...c, children: [] }));
  byId.forEach((node) => {
    if (node.parent_id && byId.has(node.parent_id)) {
      byId.get(node.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortFn = (a, b) => (b.dt?.getTime?.() ?? 0) - (a.dt?.getTime?.() ?? 0);
  const sortTree = (nodes) => {
    nodes.sort(sortFn);
    nodes.forEach((n) => sortTree(n.children));
  };
  sortTree(roots);
  return roots;
}

export default function ArticlePage() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const article = useSelector((s) => s.content.byId[String(id)]);
  const contentStatus = useSelector((s) => s.content.status);

  const user = useSelector(selectAuthUser);
  const reduxComments = useSelector((s) => selectCommentsFor(s, id));

  const [rootText, setRootText] = useState("");
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  

  // fetch article
  useEffect(() => {
    if (!article && contentStatus !== "loading") {
      dispatch(fetchContent({ id: String(id) }));
    }
  }, [id, article, contentStatus, dispatch]);

  // hydrate comments to Redux once article payload arrives
  useEffect(() => {
    if (article?.comments) {
      dispatch(
        setCommentsForContent({
          contentId: String(id),
          comments: normalizeComments(article.comments),
        })
      );
    }
  }, [article?.comments, id, dispatch]);

  const tree = useMemo(() => buildCommentTree(reduxComments), [reduxComments]);

  // add comment via thunk with optimistic insert
  const handleAddComment = useCallback(
  async ({ parentId, text }) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return false;

    // If the user isn't logged in, remember the intended post and open the modal.
    if (!user?.id) {
      setPendingAction({ parentId, text: trimmed });   // <-- keep it
      setLoginOpen(true);
      return false;                                     // don't clear the textarea
    }

    // Logged in: optimistic + thunk
    const payload = { contentId: String(id), parentId, text: trimmed };
    const userDisplay = user?.nick || user?.login || 'You';

    // optimistic comment uses ISO string (avoid non-serializable Date warning)
    dispatch(addOptimistic({ ...payload, userDisplay, dt: new Date().toISOString() }));

    try {
      await dispatch(addComment(payload)).unwrap();     // real save
      return true;                                      // caller will clear textarea
    } catch (err) {
      // roll back to current redux list on failure
      dispatch(setCommentsForContent({ contentId: String(id), comments: reduxComments }));
      console.error('addComment failed:', err);
      return false;
    }
  },
  [dispatch, id, user, reduxComments]
);


  const onLoginSuccess = () => {
  setLoginOpen(false);
  if (pendingAction) {
    const p = pendingAction;
    setPendingAction(null);
    // Re-post the original comment, then clear the textarea on success
    handleAddComment(p).then((ok) => ok && setRootText(''));
  }
};

  // ---- early returns
  if (contentStatus === "loading" && !article) {
    return <div className="container mt-4">Loading…</div>;
  }
  if (!article) {
    return (
      <div className="container mt-4">
        <SEO
          title="Article not found"
          description="The requested article could not be found."
          robots="noindex,follow"
          canonical={`${BASE_URL}/article/${encodeURIComponent(String(id))}`}
        />
        Article not found.
      </div>
    );
  }

  // SEO/meta
  const { firstParagraph, rest } = splitFirstParagraph(article.page || "");
  const title = article.topic || article.title || "Untitled";
  const publishedISO = article?.dt ? new Date(article.dt).toISOString() : null;
  const modifiedISO = article?.dt ? new Date(article.dt).toISOString() : publishedISO;
  const canonical = `${BASE_URL}/article/${encodeURIComponent(String(id))}`;
  const image = article.image || "";
  const authorName = article.author || "We Are Local";
  const section = article.type || "";
  const keywordsCsv = String(article.tags || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .join(", ");

  const leadHtml = DOMPurify.sanitize(firstParagraph || "");
  const bodyHtml = DOMPurify.sanitize(rest || "");
  const metaDescription =
    stripText(firstParagraph || article.excerpt || article.summary || article.page || title);

  console.log('Auth user from redux:', user);  

  const publisher = {
    "@type": "Organization",
    name: "We Are Local",
    url: BASE_URL,
    logo: { "@type": "ImageObject", url: `${BASE_URL}/logo.png` },
  };

  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    headline: title,
    image: image ? [image] : undefined,
    datePublished: publishedISO || undefined,
    dateModified: modifiedISO || publishedISO || undefined,
    author: [{ "@type": "Person", name: authorName }],
    publisher,
    articleSection: section || undefined,
    keywords: keywordsCsv || undefined,
    description: metaDescription || undefined,
  };

  const jsonLdBreadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Articles", item: `${BASE_URL}/articles` },
      { "@type": "ListItem", position: 3, name: title, item: canonical },
    ],
  };

  const jsonLdWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    url: canonical,
    description: metaDescription || undefined,
  };

  const og = {
    url: canonical,
    title,
    description: metaDescription,
    type: "article",
    image,
    article: {
      published_time: publishedISO || undefined,
      modified_time: modifiedISO || undefined,
      author: authorName,
      section: section || undefined,
      tag: keywordsCsv || undefined,
    },
    twitter: {
      card: image ? "summary_large_image" : "summary",
      title,
      description: metaDescription,
      image,
    },
  };

  return (
    <div className="container mt-4">
      {/* SEO */}
      <SEO
        key={canonical}
        title={title}
        description={metaDescription}
        canonical={canonical}
        robots={article.published === false ? "noindex,follow" : "index,follow"}
        og={og}
        jsonLd={[jsonLdArticle, jsonLdBreadcrumbs, jsonLdWebPage]}
      />

      {/* Header */}
      <h1>{article.topic || article.title}</h1>

      {(article.author || article.dt || article.link) && (
        <div className="mb-3 text-muted" style={{ fontSize: "0.9rem" }}>
          {article.author && <span>By {article.author}</span>}
          {article.dt && <span> {new Date(article.dt).toLocaleDateString()}</span>}
          {article.link && (
            <>
              {" · "}
              <a href={article.link} target="_blank" rel="noopener noreferrer">
                Source
              </a>
            </>
          )}
        </div>
      )}

      {article.image && (
        <img
          src={article.image}
          alt={article.topic || title}
          className="img-fluid mb-3"
          style={{ width: "100%", objectFit: "cover" }}
        />
      )}

      {article.tags && (
        <div className="mb-3">
          {String(article.tags)
            .split(",")
            .map((tag, idx) => {
              const t = tag.trim();
              if (!t) return null;
              return (
                <span
                  key={idx}
                  className="badge bg-secondary me-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => console.log(`Tag clicked: ${t}`)}
                >
                  {t}
                </span>
              );
            })}
        </div>
      )}

      {leadHtml && <div className="lead mb-3">{parse(leadHtml, parserOptions)}</div>}

      <div className="article-body">
        <Suspense fallback={null}>{parse(bodyHtml, parserOptions)}</Suspense>
      </div>

      {/* Comments */}
      <hr />
      <h4 className="mb-3">Comments ({reduxComments.length})</h4>

      {/* Top-level composer */}
      <div className="mb-3">
        <textarea
          className="form-control"
          rows="3"
          value={rootText}
          onChange={(e) => setRootText(e.target.value)}
          placeholder="Write a comment…"
        />
        <button
          className="btn btn-primary"
          disabled={!rootText.trim()}
          onClick={() =>
            handleAddComment({ parentId: null, text: rootText }).then((ok) => ok && setRootText(""))
          }
        >
          Add Comment
          </button>
      </div>

      {/* Login modal sits OUTSIDE the composer div */}
      <LoginModal
        open={loginOpen}
        onClose={() => { setLoginOpen(false); setPendingAction(null); }}
        onSuccess={onLoginSuccess}
      />

      {reduxComments.length === 0 && <p className="text-muted">No comments yet.</p>}

      <ul className="c-list">
        {tree.map((node) => (
          <ThreadItem key={node.id} node={node} onPersist={handleAddComment} />
        ))}
      </ul>

      <style>{`
        /* --- Compact comments --- */
        .c-list{margin:0;padding:0;list-style:none}
        .c-item{margin:0 0 8px 0}
        .c-card{
          background:#f9fafb;
          border:1px solid #e6e8eb;
          border-radius:10px;
          padding:8px 10px;
        }
        .c-meta{
          display:flex;
          align-items:center;
          gap:8px;
          margin:0 0 6px 0;
          flex-wrap:wrap;
        }
        .c-user{font-weight:600;font-size:.95rem}
        .c-dot{opacity:.3}
        .c-time{color:#6b7280;font-size:.8rem}
        .c-actions{margin:4px 0 0 0}
        .c-reply{padding:2px 8px;font-size:.8rem;line-height:1.2}
        .c-text{margin:0;font-size:.95rem;line-height:1.35;word-wrap:break-word}
        .c-children{margin:6px 0 0 10px;padding-left:10px;border-left:2px solid #eef0f2}
        .c-reply-box{margin-top:6px}
        .c-reply-box textarea{min-height:64px}
      `}</style>
    </div>
  );
}

function ThreadItem({ node, onPersist }) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState("");
  if (!node?.text || !node.text.trim()) return null;

  return (
    <li className="c-item">
      <div className="c-card">
        <div className="c-meta">
          <span className="c-user">{node.user || "Guest"}</span>
          <span className="c-dot">•</span>
          <span className="c-time">
            {(node.dt instanceof Date ? node.dt : new Date(node.dt)).toLocaleString()}
          </span>
          {!open && (
            <>
              <span className="c-dot">•</span>
              <button
                className="btn btn-sm btn-outline-secondary c-reply"
                onClick={() => setOpen(true)}
              >
                Reply
              </button>
            </>
          )}
        </div>

        <p className="c-text">{node.text}</p>

        {open && (
          <div className="c-reply-box">
            <textarea
              className="form-control"
              rows={2}
              placeholder="Write a reply…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="mt-2 d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={async () => {
                  const ok = await onPersist({ parentId: node.id, text });
                  if (ok) {
                    setText("");
                    setOpen(false);
                  }
                }}
              >
                Post Reply
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {node.children?.length > 0 && (
        <ul className="c-children c-list">
          {node.children.map((child) => (
            <ThreadItem key={child.id} node={child} onPersist={onPersist} />
          ))}
        </ul>
      )}
    </li>
  );
}